import { cellCenter, geo, heroWalkable } from '../grid.ts';
import { cosA, dist2, iatan2, idiv, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, DrillsRules, DrillsState, Enemy, Hero, SendaNpc, StrutsRules, StrutsState } from '../types.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { useSkills } from './ghost.ts';
import { inArc, turnOf } from '../battle/golems.ts';
import { strutAt, strutGolem, strutOrder } from '../battle/struts.ts';
import { cartOrder } from '../battle/carts.ts';
import { DRILL_LEVERS, drillOrder, drillTurnsRight } from '../battle/drills.ts';
import { DRILLS } from '../battle/ojos-layout.ts';

/**
 * The lab's bot for the artificer's chapter 4 (docs/sendas/artifice.md, «Los ojos»; the common bot in ai/senda-bot.ts
 * hands these acts here). It only moves the hero, picks its target and taps the marks, as a player's commands would.
 *
 * - the night of the promise (`struts`, the memory): who knows sends both golems to struts at once (Grillo's first),
 *   keeps by the one Bruni is wedging behind and strikes what comes at a golem first, then what goes for the houses;
 *   who doesn't sends the golems the same way but fights whatever is nearest, wherever.
 * - the carts (`carts`): who knows goes to the next crossing's brake and lets its cart go (from the brake, off the rail),
 *   steps off the ice warned under it and out of a beetle's slam, and keeps Dunna by; through, up to the next; open,
 *   to the door. Who doesn't walks up the gallery striking what is nearest (the shells too).
 * - the drills (`drills`, the test): who knows takes one wall at a time: anything else out, the belts turned for its two
 *   drills, both in; while it's cut, it keeps La Barrena on itself and shoots what thaws; then the other; then La Barrena.
 *   Who doesn't puts every drill in (the obvious, more force), and again after each jam.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

/** Shoots `t` from where it stands (no walking after it). */
const shoot = (s: BattleState, h: Hero, t: Enemy): void => {
  strike(s, h, t);
  h.forced = false;
};

/** Shoots it if in reach, else goes after it. */
const engage = (s: BattleState, h: Hero, t: Enemy): void => {
  if (withinRange(h.x, h.y, t.x, t.y, h.stats.range - 200)) return shoot(s, h, t);
  return strike(s, h, t);
};

const npc = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id && n.hp > 0);

const floorNear = (s: BattleState, x: number, y: number, r: number, ok: (p: { x: number; y: number }) => boolean = () => true): { x: number; y: number } | null => {
  let best: { x: number; y: number } | null = null;
  let bd = 0x7fffffff;
  const R = s.map.radius;
  for (let c = 0, n = geo(R).q.length; c < n; c++) {
    if (!heroWalkable(s, c)) continue;
    const p = cellCenter(R, c);
    if (!withinRange(p.x, p.y, x, y, r) || !ok(p)) continue;
    const d = dist2(p.x, p.y, x, y);
    if (d < bd) {
      bd = d;
      best = p;
    }
  }
  return best;
};

/** Off the spots warned under the hero. True if it had to. */
const offSpots = (s: BattleState, h: Hero, spots: { x: number; y: number; t: number }[], r: number): boolean => {
  const near = spots.filter((p) => withinRange(h.x, h.y, p.x, p.y, r + 250));
  if (!near.length) return false;
  const p = floorNear(s, h.x, h.y, r * 3, (c) => !spots.some((o) => withinRange(c.x, c.y, o.x, o.y, r + 400)));
  if (!p) return false;
  h.target = 0;
  go(s, h, p.x, p.y, 100);
  return true;
};

/** Out of an arc about to land (from `x, y` aimed at `aim`): to its side. True if it had to. */
const offArc = (s: BattleState, h: Hero, x: number, y: number, aim: number, reach: number, arc: number): boolean => {
  if (!inArc(x, y, aim, reach + 400, arc + 80, h.x, h.y)) return false;
  const a = aim + (turnOf(aim, iatan2(h.y - y, h.x - x)) >= 0 ? 400 : -400);
  const p = floorNear(s, x + idiv(cosA(a) * (reach + 900), 10000), y + idiv(sinA(a) * (reach + 900), 10000), 1300);
  if (!p) return false;
  h.target = 0;
  go(s, h, p.x, p.y, 100);
  return true;
};

/* ---------- the night of the promise ---------- */

/** Sends a free golem to a loose strut nobody goes to (Grillo's first); `one`: only with none set or going. */
const sendGolems = (s: BattleState, h: Hero, r: StrutsRules, w: StrutsState, one: boolean): void => {
  if (!w.golems.some((g) => g.mode === 0)) return;
  if (one && w.golems.some((g) => g.mode >= 1 && g.mode <= 3)) return;
  const loose = r.creak.filter((i) => w.st[i] === 0 && strutGolem(w, i) < 0);
  const i = loose[0];
  if (i !== undefined) strutOrder(s, h, i);
};

export const strutsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.struts;
  const w = s.senda?.struts;
  if (!r || !w) return;
  // Who doesn't know sends one golem at a time (the stair's groan and the golems' letting go mean nothing to it).
  sendGolems(s, h, r, w, !knows);
  const foes = enemiesNear(s, h.x, h.y, 16000, (e) => targetable(s, e));
  if (!knows) {
    const t = nearestTo(foes, h.x, h.y);
    if (t) return strike(s, h, t);
    h.target = 0;
    return;
  }
  // What is about to get into a house first, then what is at a golem bracing or holding, then what goes for the houses.
  const aims = new Set(w.aims.map((a) => a.id));
  const toDoor = (e: Enemy): number => r.doors.reduce((m, p) => (dist2(e.x, e.y, p.x, p.y) < m ? dist2(e.x, e.y, p.x, p.y) : m), 0x7fffffff);
  let house: Enemy | null = null;
  let hd = 0x7fffffff;
  for (const e of foes) {
    if (!aims.has(e.id)) continue;
    const d = toDoor(e);
    if (d < hd) {
      hd = d;
      house = e;
    }
  }
  if (house && hd < 3500 * 3500) return engage(s, h, house);
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  w.golems.forEach((g, i) => {
    if (g.mode < 1 || g.mode > 3) return;
    const n = npc(s, i === 0 ? 'golem1' : 'golem2');
    if (!n) return;
    for (const e of foes) {
      const d = dist2(e.x, e.y, n.x, n.y);
      if (d < 2600 * 2600 && d < bd) {
        bd = d;
        best = e;
      }
    }
  });
  if (best) return engage(s, h, best);
  if (house) return engage(s, h, house);
  const near = nearestTo(enemiesNear(s, h.x, h.y, h.stats.range - 200, (e) => targetable(s, e)), h.x, h.y);
  if (near) return shoot(s, h, near);
  h.target = 0;
  // Between the struts held and the rim (where they come from), in the middle of the ledge.
  const held = w.golems.filter((g) => g.mode >= 2 && g.mode <= 3).map((g) => strutAt(s, r, g.strut));
  const x = held.length ? idiv(held.reduce((a, p) => a + p.x, 0), held.length) : 0;
  go(s, h, x, (held[0]?.y ?? 0) + 1900, 500);
};

/* ---------- the carts ---------- */

export const cartsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.carts;
  const w = s.senda?.carts;
  if (!r || !w) return;
  if (!knows) {
    // Up the gallery after what is nearest (a beetle in its shell too).
    const t = nearestTo(enemiesNear(s, h.x, h.y, 5000, (e) => targetable(s, e)), h.x, h.y);
    if (t) return strike(s, h, t);
    h.target = 0;
    const gate = r.rails[w.open];
    if (gate) {
      const c = cellCenter(s.map.radius, gate.gate[0] as number);
      return go(s, h, c.x, c.y + 900);
    }
    return go(s, h, r.door.x, r.door.y);
  }
  const busy = w.job >= 0 && w.held > 0;
  if (!busy) {
    if (offSpots(s, h, w.spots, r.crack.r)) {
      w.job = -1;
      return;
    }
    for (const b of w.beetles) {
      if (b.wind < 0) continue;
      const e = findEnemy(s, b.id);
      if (e && offArc(s, h, e.x, e.y, b.aim, r.beetle.reach, r.beetle.arc)) {
        w.job = -1;
        return;
      }
    }
  }
  if (w.job >= 0) return;
  // Shades close by: shot (the beetles in their shells are left be).
  const shells = new Set(w.beetles.filter((b) => b.shell).map((b) => b.id));
  const near = nearestTo(enemiesNear(s, h.x, h.y, h.stats.range - 300, (e) => targetable(s, e) && !shells.has(e.id)), h.x, h.y);
  const d = npc(s, 'dunna');
  const atD = d ? nearestTo(enemiesNear(s, d.x, d.y, 1800, (e) => targetable(s, e) && !shells.has(e.id)), d.x, d.y) : null;
  if (atD) return engage(s, h, atD);
  if (near) return shoot(s, h, near);
  h.target = 0;
  // A wanderer in its shell after the hero: brought onto an open rail with a cart at its top, and the cart let go.
  const i = w.open;
  if (i < r.rails.length) {
    const cart = w.carts[i];
    if (cart?.mode === 0) return cartOrder(s, h, i);
    // Waiting for its cart (it ran clear): back out of the slams' reach.
    const c = cellCenter(s.map.radius, r.rails[i]?.brake as number);
    return go(s, h, c.x, c.y + 1700, 400);
  }
  go(s, h, r.door.x, r.door.y, 400);
};

/* ---------- the drills ---------- */

/** The wall to take now (standing): the one its drills want fewer pulls for from the belts as they are. */
const wallNow = (r: DrillsRules, w: DrillsState): number => {
  const pulls = [0, 1].map((wall) => {
    if ((w.down[wall] as number) >= 0) return 99;
    let n = 0;
    for (const b of [0, 1]) {
      const d = r.drills.findIndex((x, k) => x.wall === wall && (k < 2 ? 0 : 1) === b);
      if (d >= 0 && !drillTurnsRight(r, w, d)) n++;
    }
    return n;
  });
  return (pulls[0] as number) <= (pulls[1] as number) ? 0 : 1;
};

/** One step toward a wall's two drills right and in, nothing else in. False when it's set (cutting). */
const drillStep = (s: BattleState, h: Hero, r: DrillsRules, w: DrillsState, wall: number): boolean => {
  if (w.job >= 0) return true;
  const other = r.drills.findIndex((x, d) => w.inn[d] && x.wall !== wall);
  if (other >= 0) {
    drillOrder(s, h, other);
    return true;
  }
  const ds = [0, 1, 2, 3].filter((d) => r.drills[d]?.wall === wall);
  const wrong = ds.find((d) => !drillTurnsRight(r, w, d));
  if (wrong !== undefined) {
    // (A drill in on the belt about to turn is fine: its partner is the other wall's, out.)
    drillOrder(s, h, DRILL_LEVERS + (wrong < 2 ? 0 : 1));
    return true;
  }
  const out = ds.find((d) => !w.inn[d]);
  if (out !== undefined) {
    drillOrder(s, h, out);
    return true;
  }
  return false;
};

export const drillsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.drills;
  const w = s.senda?.drills;
  if (!r || !w) return;
  const bz = w.bz.mode === 1 ? findEnemy(s, w.bz.id) : undefined;
  const standing = (w.down[0] as number) < 0 || (w.down[1] as number) < 0;
  if (!knows) {
    const t = nearestTo(enemiesNear(s, h.x, h.y, 1600, (e) => targetable(s, e)), h.x, h.y);
    if (t && w.job < 0) return strike(s, h, t);
    if (standing && w.job < 0) {
      // Everything in: more force (the obvious).
      const out = [0, 1, 2, 3].find((d) => !w.inn[d]);
      if (out !== undefined) return drillOrder(s, h, out);
    }
    if (w.job >= 0) return;
    const any = nearestTo(enemiesNear(s, h.x, h.y, 12000, (e) => targetable(s, e)), h.x, h.y);
    if (any) return strike(s, h, any);
    h.target = 0;
    return;
  }
  const busy = w.job >= 0 && w.held > 0;
  if (!busy) {
    if (bz && w.bz.wind >= 0 && offArc(s, h, bz.x, bz.y, w.bz.aim, r.boss.reach, r.boss.arc)) {
      w.job = -1;
      return;
    }
    if (offSpots(s, h, w.spots, r.pulse.r)) {
      w.job = -1;
      return;
    }
  }
  if (!standing) {
    const t = bz ?? nearestTo(enemiesNear(s, h.x, h.y, 12000, (e) => targetable(s, e)), h.x, h.y);
    if (t) return engage(s, h, t);
    h.target = 0;
    return;
  }
  // Frozen ones at the hero or at Dunna first (not while a clutch is half worked).
  if (w.job < 0) {
    const near = nearestTo(enemiesNear(s, h.x, h.y, 1500, (e) => targetable(s, e) && e.id !== w.bz.id), h.x, h.y);
    if (near) return shoot(s, h, near);
    const d = npc(s, 'dunna');
    const atD = d ? nearestTo(enemiesNear(s, d.x, d.y, 2000, (e) => targetable(s, e) && e.id !== w.bz.id), d.x, d.y) : null;
    if (atD) return engage(s, h, atD);
  }
  const wall = wallNow(r, w);
  if (drillStep(s, h, r, w, wall)) return;
  // Cutting: La Barrena kept on the hero (it goes for the hero within its chase), shot from out of its reach.
  if (bz) {
    if (withinRange(h.x, h.y, bz.x, bz.y, r.boss.reach + 300)) {
      const a = iatan2(h.y - bz.y, h.x - bz.x);
      const p = floorNear(s, bz.x + idiv(cosA(a) * (r.boss.reach + 900), 10000), bz.y + idiv(sinA(a) * (r.boss.reach + 900), 10000), 1200);
      if (p) {
        h.target = 0;
        return go(s, h, p.x, p.y, 150);
      }
    }
    if (withinRange(h.x, h.y, bz.x, bz.y, h.stats.range - 200)) return shoot(s, h, bz);
    return strike(s, h, bz);
  }
  const t = nearestTo(enemiesNear(s, h.x, h.y, h.stats.range - 200, (e) => targetable(s, e)), h.x, h.y);
  if (t) return shoot(s, h, t);
  h.target = 0;
};

/** (The drills' marks: DRILLS clutches then the two levers.) */
export const DRILL_MARKS = DRILLS + 2;
